/**
 * Paint Collection Picker
 * Multi-select paints on a collection page and add them to the basket in one go.
 */

(function () {
  'use strict';

  const root = document.querySelector('.paint-collection-picker');
  if (!root) return;

  const bar = root.querySelector('[data-paint-picker-bar]');
  const countEl = root.querySelector('[data-paint-selected-count]');
  const totalEl = root.querySelector('[data-paint-selected-total]');
  const addButton = root.querySelector('[data-paint-add]');
  const clearButton = root.querySelector('[data-paint-clear-selection]');
  const selectedTray = root.querySelector('[data-paint-selected-tray]');
  const moneyFormat = root.dataset.moneyFormat || '£{{amount}}';

  const selected = new Map();

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    const amount = (Number(cents) / 100).toFixed(2);
    return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '').trim() || `£${amount}`;
  }

  function checkboxes() {
    return root.querySelectorAll('[data-paint-checkbox]');
  }

  function renderSelectedTray() {
    if (!selectedTray) return;
    selectedTray.innerHTML = '';

    selected.forEach((item, id) => {
      const paint = document.createElement('div');
      paint.className = 'paint-picker__selected-paint';
      paint.title = item.title;

      const swatch = document.createElement('span');
      swatch.className = 'paint-picker__selected-swatch';
      swatch.style.backgroundColor = item.color;
      swatch.setAttribute('aria-hidden', 'true');

      const name = document.createElement('span');
      name.className = 'paint-picker__selected-name';
      name.textContent = item.title;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'paint-picker__selected-remove';
      remove.dataset.removePaint = id;
      remove.setAttribute('aria-label', `Remove ${item.title}`);
      remove.textContent = '×';

      paint.append(swatch, name, remove);
      selectedTray.appendChild(paint);
    });
  }

  function render() {
    let cents = 0;
    selected.forEach((item) => { cents += item.price; });

    if (countEl) countEl.textContent = selected.size;
    if (totalEl) totalEl.textContent = formatMoney(cents);
    if (addButton) addButton.disabled = selected.size === 0;
    if (bar) bar.hidden = selected.size === 0;
    renderSelectedTray();
  }

  function syncCheckbox(checkbox) {
    const option = checkbox.closest('[data-paint-option]');
    if (!option) return;
    const id = option.dataset.paintId;
    if (checkbox.checked) {
      selected.set(id, {
        variantId: parseInt(option.dataset.variantId, 10),
        price: Number(option.dataset.price) || 0,
        color: option.dataset.paintColor || '#CCCCCC',
        title: option.querySelector('.paint-card__title')?.textContent.trim() || 'Paint'
      });
    } else {
      selected.delete(id);
    }
  }

  root.addEventListener('change', (event) => {
    if (event.target.matches('[data-paint-checkbox]')) {
      syncCheckbox(event.target);
      render();
    }
  });

  root.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-remove-paint]');
    if (!removeButton) return;

    const id = removeButton.dataset.removePaint;
    selected.delete(id);
    const checkbox = root.querySelector(`[data-paint-option][data-paint-id="${CSS.escape(id)}"] [data-paint-checkbox]`);
    if (checkbox) checkbox.checked = false;
    render();
  });

  if (clearButton) {
    clearButton.addEventListener('click', () => {
      selected.clear();
      checkboxes().forEach((checkbox) => { checkbox.checked = false; });
      render();
    });
  }

  if (addButton) {
    addButton.addEventListener('click', async () => {
      if (!selected.size) return;

      const textEl = addButton.querySelector('.custom-btn__text');
      const originalText = textEl.textContent;
      addButton.disabled = true;
      textEl.textContent = 'ADDING...';

      const items = Array.from(selected.values()).map((item) => ({
        id: item.variantId,
        quantity: 1
      }));

      try {
        const cartItemsComponents = document.querySelectorAll('cart-items-component[data-section-id]');
        const sectionIds = Array.from(cartItemsComponents).map((component) => component.dataset.sectionId);
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });

        if (!addResponse.ok) {
          const errorData = await addResponse.json();
          throw new Error(errorData.message || 'Failed to add items to cart');
        }

        const addResponseData = await addResponse.json();
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        let sections = addResponseData.sections || {};

        // Some Shopify responses do not return sections from cart/add.js.
        // Render the cart sections through cart/update.js so the existing
        // cart-items-component can morph the drawer without a page refresh.
        if (sectionIds.length > 0 && Object.keys(sections).length === 0) {
          const sectionResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              updates: {},
              sections: sectionIds.join(','),
              sections_url: window.location.pathname
            })
          });
          const sectionData = await sectionResponse.json();
          sections = sectionData.sections || {};
        }

        textEl.textContent = 'ADDED!';

        selected.clear();
        checkboxes().forEach((checkbox) => { checkbox.checked = false; });

        dispatchCartUpdate(cart, sections);

        setTimeout(() => {
          textEl.textContent = originalText;
          render();
        }, 2000);
      } catch (error) {
        console.error('Error adding paints to cart:', error);

        let errorMessage = 'ERROR - TRY AGAIN';
        if (error.message.includes('sold out') || error.message.includes('inventory')) {
          errorMessage = 'SOME PAINTS NO LONGER AVAILABLE';
        }
        textEl.textContent = errorMessage;

        setTimeout(() => {
          textEl.textContent = originalText;
          addButton.disabled = selected.size === 0;
        }, 3000);
      }
    });
  }

  function dispatchCartUpdate(cart, sections) {
    // ThemeEvents.cartUpdate is consumed by cart-icon, cart-items-component,
    // product forms, and the cart drawer. Keep the event shape identical to
    // the theme's native product-form add-to-cart flow.
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: root.id,
        data: {
          source: 'paint-collection-picker',
          itemCount: cart.item_count,
          sections
        }
      }
    }));

    // Keep the legacy event for any custom theme integrations.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  render();
}());
